// frontend/src/components/InteractiveFloorPlan/InteractiveFloorPlan.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from './InteractiveFloorPlan.module.css';

const InteractiveFloorPlan = ({ imageName, imageAlt, mapName, areas = [], onAreaSelect, onBackgroundClick }) => {
  // This state is internal and tracks which area is persistently highlighted
  const [selectedArea, setSelectedArea] = useState(null);

  // Refs and state for rendering the map
  const [scaledAreas, setScaledAreas] = useState([]);
  const imageRef = useRef(null);
  const canvasRef = useRef(null);

  // Helper function to parse coordinate strings into points
  const parseCoords = useCallback((coordsString) => {
    const coordsArray = coordsString.split(',');
    const points = [];
    for (let i = 0; i < coordsArray.length; i += 2) {
      points.push({ x: parseInt(coordsArray[i], 10), y: parseInt(coordsArray[i + 1], 10) });
    }
    return points;
  }, []);

  // Helper function to draw a highlight on the canvas for a given area
  const drawHighlight = useCallback((area, styleOptions = {}) => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    if (!canvas || !image || !area || !image.naturalWidth) return;
    const ctx = canvas.getContext('2d');
    const points = parseCoords(area.coords);
    const scaleX = image.clientWidth / image.naturalWidth;
    const scaleY = image.clientHeight / image.naturalHeight;
    ctx.beginPath();
    if (area.shape === 'rect' && points.length === 2) {
      const [p1, p2] = points;
      ctx.rect(p1.x * scaleX, p1.y * scaleY, (p2.x - p1.x) * scaleX, (p2.y - p1.y) * scaleY);
    } else if (area.shape === 'poly') {
      ctx.moveTo(points[0].x * scaleX, points[0].y * scaleY);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x * scaleX, points[i].y * scaleY);
      }
      ctx.closePath();
    }

    // Use default active styles unless overrides are provided
    ctx.fillStyle = styleOptions.fillStyle || 'rgba(0, 123, 255, 0.2)';
    ctx.strokeStyle = styleOptions.strokeStyle || 'rgba(0, 123, 255, 0.8)';
    ctx.lineWidth = styleOptions.lineWidth || 2;

    ctx.fill();
    ctx.stroke();
  }, [parseCoords]);

  // Helper function to draw all areas with a subtle default highlight
  const drawAllHighlights = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear first

    // Draw each area with a subtle style
    areas.forEach(area => {
      drawHighlight(area, {
        fillStyle: 'rgba(0, 123, 255, 0.05)', // Very faint blue
        strokeStyle: 'rgba(0, 123, 255, 0.3)', // Faint border
        lineWidth: 1
      });
    });
  }, [areas, drawHighlight]);

  // Helper function to clear any highlight from the canvas and restore defaults
  const clearHighlight = useCallback(() => {
    drawAllHighlights();
  }, [drawAllHighlights]);

  // Handles clicks on a specific mapped area
  const handleAreaClick = (e, area) => {
    e.stopPropagation(); // Prevents the click from bubbling up to the background handler
    setSelectedArea(area); // Set the internal state to this area
    if (onAreaSelect) {
      onAreaSelect(area); // Notify the parent component
    }
    // Clear everything and then draw the specific selected highlight
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    }
    drawHighlight(area);
  };

  // Handles mouse hover over a mapped area
  const handleAreaMouseOver = (area) => {
    // We clear all default highlights so the hovered one stands out completely
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    }
    drawHighlight(area);
  };

  // Handles mouse leaving a mapped area
  const handleAreaMouseOut = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    }
    // If an area was permanently selected, redraw its highlight. Otherwise, restore defaults.
    if (selectedArea) {
      drawHighlight(selectedArea);
    } else {
      drawAllHighlights();
    }
  };

  // NEW: Handles clicks on the background (non-mapped parts of the image)
  const handleBackgroundClick = () => {
    setSelectedArea(null); // Clear the internal selected area state
    clearHighlight();      // Clear the canvas highlight
    if (onBackgroundClick) {
      onBackgroundClick(); // Notify the parent component to reset its view
    }
  };

  // Recalculates coordinates when the image or window size changes
  const updateLayoutAndCoords = useCallback(() => {
    const image = imageRef.current;
    const canvas = canvasRef.current;
    if (!image || !canvas || !image.naturalWidth || areas.length === 0) return;
    canvas.width = image.clientWidth;
    canvas.height = image.clientHeight;
    const scaleX = image.clientWidth / image.naturalWidth;
    const scaleY = image.clientHeight / image.naturalHeight;
    const newScaledAreas = areas.map(area => {
      const originalCoords = area.coords.split(',').map(Number);
      const scaledCoords = originalCoords.map((coord, index) => {
        return index % 2 === 0 ? Math.round(coord * scaleX) : Math.round(coord * scaleY);
      });
      return { ...area, scaledCoords: scaledCoords.join(',') };
    });
    setScaledAreas(newScaledAreas);

    // Draw initial highlights once coordinates are set
    setTimeout(() => {
      // Small timeout ensures the hook flow is complete before drawing the first time
      drawAllHighlights();
    }, 50);
  }, [areas, drawAllHighlights]);

  // Effect to set up and tear down event listeners
  useEffect(() => {
    const image = imageRef.current;
    const handleLoad = () => { updateLayoutAndCoords(); };
    if (image?.complete) {
      handleLoad();
    } else {
      image?.addEventListener('load', handleLoad);
    }
    window.addEventListener('resize', updateLayoutAndCoords);

    // Clear selection when props change (e.g., user switches floor)
    setSelectedArea(null);
    clearHighlight();

    return () => {
      image?.removeEventListener('load', handleLoad);
      window.removeEventListener('resize', updateLayoutAndCoords);
    };
  }, [updateLayoutAndCoords, areas, clearHighlight]);

  return (
    // The onClick handler is now on the container div
    <div className={styles.floorPlanContainer} onClick={handleBackgroundClick}>
      <img ref={imageRef} src={`/images/floorplans/${imageName}`} alt={imageAlt} useMap={`#${mapName}`} key={imageName} />
      <canvas ref={canvasRef} className={styles.highlightCanvas} />
      <map name={mapName}>
        {scaledAreas.map((area) => (
          <area
            key={area.id}
            coords={area.scaledCoords}
            shape={area.shape}
            href="#"
            alt={area.title}
            title={area.title}
            // Pass the event object to the click handler
            onClick={(e) => { e.preventDefault(); handleAreaClick(e, area); }}
            onMouseOver={() => handleAreaMouseOver(area)}
            onMouseOut={handleAreaMouseOut}
          />
        ))}
      </map>
    </div>
  );
};

export default InteractiveFloorPlan;
